import { Component, ElementRef, OnInit, ViewChild, isDevMode } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CheckValidSessionUseCase,
  CreateUniqueIdUseCase,
  GetUserAccountsUseCase,
  LogInUseCase,
  LoginAuthorizationError,
  LoginConnectionError,
  LoginPasswordRestoreRequiredError,
  LoginUserNotConfirmedError,
  ValidateSSOOrganizationUseCase
} from '@core/core';

import { CookieService } from '@core/auth/infrastructure/cookie.service';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Failure, Success, UnknownError } from '@sdk/sdk';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent implements OnInit {
  private _redirectTo: string | null = null;

  form: FormGroup;
  ssoForm: FormGroup;
  hasError = false;
  errorMessage = '';
  isLoading = false;
  initializing = true;
  isDevMode = false;
  passToggleIcon: IconName = 'eye';
  passShown = false;
  isSSO = false;
  domain = '';
  webClientId = '';
  production = false;
  cognitoIdentityProviderUrl = '';

  @ViewChild('email') emailInput!: ElementRef;
  @ViewChild('password') passInput!: ElementRef;

  public get isFormValid(): boolean {
    return this.form.valid;
  }

  public get isSSOFormValid(): boolean {
    return this.ssoForm.valid;
  }

  public get emailValue(): string | undefined {
    return this.form.value.email ? this.form.value.email : undefined;
  }

  constructor(
    private _loginUseCase: LogInUseCase,
    private _checkSessionUseCase: CheckValidSessionUseCase,
    private _getUserAccountsUseCase: GetUserAccountsUseCase,
    private _createUniqueIdUseCase: CreateUniqueIdUseCase,
    private _validateSSOOrganizationUseCase: ValidateSSOOrganizationUseCase,
    private _router: Router,
    private _route: ActivatedRoute,
    private _cookieService: CookieService
  ) {
    this.form = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required])
    });

    this.ssoForm = new FormGroup({
      organization: new FormControl('', [Validators.required])
    });
  }

  private async _postToTokenEndpoint(code: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('code', code);
    params.append('client_id', this.webClientId);
    params.append('redirect_uri', environment.sso.cognito_callback_url);

    const baseUrl = environment.sso.cognito_token_endpoint_url;

    try {
      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      if (!response.ok) {
        throw new Error('Token endpoint response was not ok');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error posting to token endpoint:', error);
      throw error;
    }
  }

  async ngOnInit(): Promise<void> {
    this.isDevMode = isDevMode();
    this._createUniqueIdUseCase.execute();
    const redirected = await this._handleStartupRedirections();
    const cognitoSsoCode = this._route.snapshot.queryParamMap.get('code');
    this.domain = environment.cognito.cookie_domain;
    this.cognitoIdentityProviderUrl = environment.sso.cognito_identity_provider_url;
    this.webClientId = environment.cognito.client_id;
    this.initializing = false;

    this._loadSavedOrganization();

    if (cognitoSsoCode) {
      try {
        const data = await this._postToTokenEndpoint(cognitoSsoCode);
        await this._handleCognitoCallback(data);
        this._router.navigate(['organizations'], {
          relativeTo: this._route,
          queryParams: { r: this._redirectTo }
        });
      } catch (error) {
        this.hasError = true;
        this.errorMessage = $localize`Something went wrong during login. Please try again.`;
      }
    }

    if (redirected) {
      return;
    }

    // Handle sso_org query parameter for auto-filling SSO form
    const ssoOrg = this._route.snapshot.queryParamMap.get('sso_org');
    if (ssoOrg) {
      this.isSSO = true;
      this.ssoForm.patchValue({ organization: ssoOrg });
      
      // Auto-submit after a short delay to allow form validation to complete
      setTimeout(() => {
        if (this.isSSOFormValid && !this.isLoading && !this.initializing) {
          this.onSSOSubmit();
        }
      }, 400);
    }

    this._redirectTo = this._route.snapshot.queryParamMap.get('r');
  }

  showHidePassword(): void {
    this.passShown = !this.passShown;
    this.passToggleIcon = this.passShown ? 'eye-slash' : 'eye';
    this.passInput.nativeElement.type = this.passShown ? 'text' : 'password';
  }

  onSubmit(): void {
    if (!this.isFormValid) {
      return;
    }

    if (this.isLoading) {
      return;
    }

    this._login(this.form.value.email, this.form.value.password);
  }

  async onSSOSubmit(): Promise<void> {
    if (!this.isSSOFormValid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.ssoForm.disable();

    try {
      const organization = this.ssoForm.value.organization;
      const result = await this._validateSSOOrganizationUseCase.execute(organization);
      if (result instanceof Failure) {
        this.hasError = true;
        this.errorMessage = $localize`Invalid organization`;
        this.isLoading = false;
        this.ssoForm.enable();
        return;
      }
      localStorage.setItem('sso-organization', organization);
      const redirectLink = this._getRedirectLink(organization);
      window.location.href = redirectLink;
    } catch (error) {
      this.hasError = true;
      this.errorMessage = $localize`Something went wrong. Please try again later.`;
    } finally {
      this.isLoading = false;
      this.ssoForm.enable();
    }
  }

  private _loadSavedOrganization(): void {
    const savedOrganization = localStorage.getItem('sso-organization');
    if (savedOrganization) {
      this.ssoForm.patchValue({ organization: savedOrganization });
    }
  }

  private _getRedirectLink(organization: string): string {
    const redirectLink = this.cognitoIdentityProviderUrl.replace('USERCLIENTID', this.webClientId);
    const redirectLinkWithOrganization = redirectLink.replace('USERINPUT', organization);
    return redirectLinkWithOrganization;
  }

  private async _handleStartupRedirections(): Promise<boolean> {
    const hasSession = await this._checkHasSession();
    if (hasSession) {
      this._router.navigate(['login', 'organizations'], {
        queryParamsHandling: 'merge'
      });
      return true;
    }

    const newLogin = this._route.snapshot.queryParamMap.get('new');
    const autofillEmail = this._route.snapshot.queryParamMap.get('email');

    const stay = newLogin || autofillEmail != null;

    const accountsResult = await this._getUserAccountsUseCase.execute();
    const hasAccountCache = accountsResult.value.length > 0;

    if (hasAccountCache && !stay) {
      this._router.navigate(['login', 'accounts'], {
        queryParamsHandling: 'merge'
      });
      return true;
    }

    if (autofillEmail) {
      this.form.patchValue({ email: autofillEmail });
      this.passInput.nativeElement?.focus();
    } else {
      this.emailInput.nativeElement?.focus();
    }

    return false;
  }

  private async _checkHasSession(): Promise<boolean> {
    const result = await this._checkSessionUseCase.execute();
    if (result instanceof Success) {
      return true;
    }

    return false;
  }

  private async _login(email: string, password: string): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.form.disable();

    const response = await this._loginUseCase.execute(email, password);

    this.isLoading = false;
    this.form.enable();

    if (response instanceof Success) {
      this._router.navigate(['organizations'], {
        relativeTo: this._route,
        queryParams: { r: this._redirectTo }
      });
      return;
    }

    // We know we have a failure
    this.hasError = true;

    // Set error message based on error type
    if (response.error instanceof LoginConnectionError) {
      this.errorMessage = $localize`Connection error`;
      return;
    }

    if (response.error instanceof LoginAuthorizationError) {
      this.errorMessage = $localize`Invalid email or password`;
      return;
    }

    if (response.error instanceof LoginPasswordRestoreRequiredError) {
      this._router.navigate(['password-challenge'], {
        relativeTo: this._route,
        queryParams: { r: this._redirectTo }
      });
      return;
    }

    if (response.error instanceof LoginUserNotConfirmedError) {
      this._router.navigate(['create', 'validate'], {
        queryParams: { email: this.form.value.email }
      });

      return;
    }

    if (response.error instanceof UnknownError) {
      this.errorMessage = $localize`Something went wrong. Please try again later. If the problem persists, contact support.`;
      return;
    }
  }

  private async _handleCognitoCallback(tokens: {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }): Promise<void> {
    const idToken = new CognitoIdToken({ IdToken: tokens.id_token });
    const accessToken = new CognitoAccessToken({ AccessToken: tokens.access_token });
    const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refresh_token });

    const session = new CognitoUserSession({
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken
    });
    const expirationDate = new Date();
    expirationDate.setSeconds(expirationDate.getSeconds() + tokens.expires_in);

    const userData = {
      Username: session.getIdToken().payload['email'],
      Pool: new CognitoUserPool({
        UserPoolId: environment.cognito.user_pool_id,
        ClientId: environment.cognito.client_id
      })
    };
    const cognitoUser = new CognitoUser(userData);
    cognitoUser.setSignInUserSession(session);
    const domain = environment.cognito.cookie_domain;
    const email = session.getIdToken().payload['email'];
    const encodedEmail = encodeURIComponent(email);

    // Store tokens in localStorage for Amplify to recognize the session
    const cacheKey = `CognitoIdentityServiceProvider.${environment.cognito.client_id}.${encodedEmail}.idToken`;
    const cacheValue = tokens.id_token;
    this._cookieService.setCookieWithDate(cacheKey, cacheValue, expirationDate, domain);

    const accessTokenKey = `CognitoIdentityServiceProvider.${environment.cognito.client_id}.${encodedEmail}.accessToken`;
    this._cookieService.setCookieWithDate(accessTokenKey, tokens.access_token, expirationDate, domain);

    const refreshTokenKey = `CognitoIdentityServiceProvider.${environment.cognito.client_id}.${encodedEmail}.refreshToken`;
    this._cookieService.setCookieWithDate(refreshTokenKey, tokens.refresh_token, expirationDate, domain);

    const tokenExpirationKey = `CognitoIdentityServiceProvider.${environment.cognito.client_id}.${encodedEmail}.clockDrift`;
    this._cookieService.setCookieWithDate(tokenExpirationKey, '0', expirationDate, domain);

    // Store the last authenticated user for Amplify
    const lastAuthUserKey = `CognitoIdentityServiceProvider.${environment.cognito.client_id}.LastAuthUser`;
    this._cookieService.setCookieWithDate(
      lastAuthUserKey,
      session.getIdToken().payload['email'],
      expirationDate,
      domain
    );

    const userInfo = {
      attributes: {
        email: session.getIdToken().payload['email'],
        avatar: session.getIdToken().payload['picture'] || undefined
      }
    };
    this._cookieService.setCookieWithDate('sytex-user', JSON.stringify(userInfo), expirationDate, domain);
  }
}
